/* Lynqu store links keep rel="noopener" (not "noreferrer") to preserve the
   referrer for attribution + dofollow value. */
/* eslint-disable react/jsx-no-target-blank */
import React from "react";
import LynquIcon from "../../assets/lynqu/lynqu-icon.webp";
import FrbIcon from "../../assets/frb/frb-icon.svg";

/* Inline SVGs (no emoji icons). 24×24, stroke-based, inherit currentColor. */
function IconDevices() {
  return (
    <svg viewBox='0 0 24 24' width='24' height='24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      <rect x='2' y='4' width='14' height='10' rx='1.5' />
      <path d='M2 17h14' />
      <rect x='17.5' y='8' width='4.5' height='12' rx='1' />
    </svg>
  );
}
function IconShield() {
  return (
    <svg viewBox='0 0 24 24' width='24' height='24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      <path d='M12 3 5 6v5c0 4.4 3 7.6 7 9 4-1.4 7-4.6 7-9V6l-7-3Z' />
      <path d='m9.5 12 1.8 1.8 3.4-3.6' />
    </svg>
  );
}
function IconSpark() {
  return (
    <svg viewBox='0 0 24 24' width='24' height='24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      <path d='M12 3v4M12 17v4M3 12h4M17 12h4' />
      <path d='M12 8.5a3.5 3.5 0 0 0 3.5 3.5A3.5 3.5 0 0 0 12 15.5 3.5 3.5 0 0 0 8.5 12 3.5 3.5 0 0 0 12 8.5Z' />
    </svg>
  );
}

/* The shared FOCUS_RING is brand orange, which vanishes on the orange featured
   card — these rows need a white ring instead. */
const ROW_FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#CB4909]";

/* TRUST STRIP — TRUE statements about the agency (not invented metrics). */
const TRUST_ITEMS = [
  { key: "platforms", stat: "Mobile · Web · Backend · Cloud", label: "End-to-end across the whole stack — we design & engineer it all", Icon: IconDevices },
  // The featured tile names each product on its own row instead of running
  // both into one sentence; `products` is what makes it render that way.
  {
    key: "product",
    stat: "Two products of our own",
    featured: true,
    products: [
      { name: "Lynqu", note: "App Store & Google Play", href: "https://lynqu.com", icon: LynquIcon },
      { name: "Flat Rent Budapest", note: "On the web", href: "https://flatrentbudapest.com", icon: FrbIcon },
    ],
  },
  { key: "confidential", stat: "100% confidential", label: "Every client project, protected by default", Icon: IconShield },
  { key: "founder", stat: "Founder-led", label: "A senior team — no handoffs to juniors", Icon: IconSpark },
];

/* TESTIMONIALS — intentionally EMPTY. Add real, attributable client quotes here
   and this block appears automatically. Do NOT ship invented quotes. */
const TESTIMONIALS = [];

export default function SocialProof(props) {
  const inView = props.inView;

  /* `transition-[opacity,transform]`, not `transition-all`, and the observer
   * behind `inView` is triggerOnce so the entrance does not replay on every
   * scroll back past the section. */
  const reveal = (delay) => ({
    className: `transition-[opacity,transform] duration-700 ease-out ${
      inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
    }`,
    style: { transitionDelay: `${delay}ms` },
  });

  return (
    <section
      ref={props.sectionRef}
      id='why-gravisun'
      className='relative w-full nav-sm:min-h-[100svh] overflow-hidden bg-[#f4f3f0] flex flex-col items-center justify-center py-[80px] c-lg:py-[120px] px-[24px] c-lg:px-[64px]'>
      {/* Decorative depth: warm glow + faint dot grid (no layout impact) */}
      <div
        className='pointer-events-none absolute -top-[10%] -right-[8%] w-[46%] aspect-square rounded-full opacity-60'
        style={{ background: "radial-gradient(circle, rgba(225,85,17,0.16), transparent 65%)" }}
      />
      <div
        className='pointer-events-none absolute -bottom-[14%] -left-[10%] w-[40%] aspect-square rounded-full opacity-50'
        style={{ background: "radial-gradient(circle, rgba(203,73,9,0.12), transparent 65%)" }}
      />
      <div
        className='pointer-events-none absolute inset-0 opacity-[0.5]'
        style={{
          backgroundImage: "radial-gradient(rgba(41,41,41,0.06) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
          maskImage: "radial-gradient(ellipse 80% 60% at 50% 45%, #000 40%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 45%, #000 40%, transparent 100%)",
        }}
      />

      {/* props.revealRef is what drives `inView`. Without it the observer
          watches nothing, `inView` never flips, and every child stays at
          opacity-0 — the section renders as an empty band. */}
      <div ref={props.revealRef} className='relative z-10 w-full max-w-[1120px] mx-auto'>
        {/* Eyebrow chip */}
        <div {...reveal(0)} className={`${reveal(0).className} flex justify-center`}>
          <span className='inline-flex items-center gap-[8px] rounded-full border border-[#E15511]/20 bg-[#E15511]/[0.07] pl-[10px] pr-[16px] py-[6px]'>
            <span className='relative flex h-[8px] w-[8px]'>
              <span className='absolute inline-flex h-full w-full rounded-full bg-[#E15511] opacity-60 animate-ping motion-reduce:animate-none' />
              <span className='relative inline-flex h-[8px] w-[8px] rounded-full bg-[#E15511]' />
            </span>
            <span className='text-12 c-lg:text-13 font-semibold tracking-[0.18em] uppercase text-[#B33D06]'>
              Why Gravisun
            </span>
          </span>
        </div>

        <h2
          {...reveal(70)}
          className={`${reveal(70).className} mt-[20px] text-center font-bold text-[#1a1a1a] text-30 c-md:text-42 c-xl:text-50 leading-[1.1] tracking-[-0.01em]`}>
          A studio that{" "}
          <span className='relative inline-block text-[#E15511]'>
            ships
            {/* Inline gradient — Tailwind bg-gradient-* utilities don't compile
                in this CRA/Tailwind build (resolve to background-image:none). */}
            <span
              className='absolute left-0 -bottom-[2px] h-[3px] w-full rounded-full'
              style={{ backgroundImage: "linear-gradient(90deg, #E15511 0%, #CB4909 100%)" }}
            />
          </span>{" "}
          — and stands behind the work.
        </h2>

        {/* Bento cards */}
        <div
          {...reveal(150)}
          className={`${reveal(150).className} mt-[44px] c-lg:mt-[64px] grid grid-cols-1 sm:grid-cols-2 c-lg:grid-cols-4 gap-[16px] c-lg:gap-[20px]`}>
          {TRUST_ITEMS.map((item) =>
            item.featured ? (
              <div
                key={item.key}
                style={{ backgroundImage: "linear-gradient(135deg, #E15511 0%, #B53e07 100%)" }}
                className='group relative overflow-hidden rounded-[24px] p-[24px] flex flex-col justify-center text-white shadow-[0_24px_50px_-22px_rgba(203,73,9,0.6)] transition-transform duration-300 hover:-translate-y-[6px]'>
                {/* sheen */}
                <div className='pointer-events-none absolute -top-1/2 -right-1/3 w-[120%] h-[120%] rounded-full bg-white/[0.08] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500' />
                <div className='relative'>
                  <p className='text-22 c-lg:text-24 font-bold leading-[1.15]'>{item.stat}</p>
                  {/* Each product gets its own row, with its own icon and its own
                      link — the single-icon tile the other cards use cannot hold
                      two brands, and a semicolon list reads as an afterthought. */}
                  <ul className='mt-[18px] flex flex-col gap-[6px]'>
                    {item.products.map((prod) => (
                      <li key={prod.name}>
                        <a
                          href={prod.href}
                          target='_blank'
                          rel='noopener'
                          className={`group/row flex items-center gap-[11px] -mx-[8px] px-[8px] py-[7px] min-h-[44px] rounded-[13px] hover:bg-white/[0.14] transition-colors duration-200 ${ROW_FOCUS_RING}`}>
                          <img
                            src={prod.icon}
                            alt=''
                            aria-hidden='true'
                            width={128}
                            height={128}
                            className='w-[32px] h-[32px] rounded-[9px] shrink-0 shadow-[0_4px_10px_-3px_rgba(0,0,0,0.5)]'
                          />
                          <span className='min-w-0'>
                            <span className='block text-14 font-semibold leading-tight'>{prod.name}</span>
                            <span className='block text-12 text-white/75 leading-tight mt-[2px]'>{prod.note}</span>
                          </span>
                          <svg
                            viewBox='0 0 24 24'
                            width='15'
                            height='15'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            aria-hidden='true'
                            className='ml-auto shrink-0 text-white/55 group-hover/row:text-white transition-[color,transform] duration-200 group-hover/row:translate-x-[2px] group-hover/row:-translate-y-[2px]'>
                            <path d='M7 17 17 7' />
                            <path d='M7 7h10v10' />
                          </svg>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div
                key={item.key}
                className='group relative overflow-hidden rounded-[24px] bg-white border border-[#ebe9e4] p-[26px] flex flex-col justify-between transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-[6px] hover:border-[#E15511]/30 hover:shadow-[0_24px_50px_-26px_rgba(225,85,17,0.45)]'>
                {/* animated top accent — inline gradient (Tailwind bg-gradient-* unavailable here) */}
                <span
                  /* scaleX, not width: the accent slides in without a
                     layout pass per frame. */
                  className='absolute top-0 left-0 h-[3px] w-full origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-[450ms] ease-out'
                  style={{ backgroundImage: "linear-gradient(90deg, #E15511 0%, #CB4909 100%)" }}
                />
                <div className='w-[52px] h-[52px] rounded-[16px] bg-[#E15511]/[0.08] text-[#E15511] grid place-items-center mb-[20px] transition-colors duration-300 group-hover:bg-[#E15511]/[0.14]'>
                  <item.Icon />
                </div>
                <div>
                  <p className='text-20 c-lg:text-22 font-bold text-[#1a1a1a] leading-tight'>{item.stat}</p>
                  <p className='mt-[10px] text-13 c-lg:text-14 text-[#595959] leading-[1.5]'>{item.label}</p>
                </div>
              </div>
            )
          )}
        </div>

        {/* Testimonials — only renders when real quotes are added above */}
        {TESTIMONIALS.length > 0 && (
          <div className='mt-[56px] c-lg:mt-[72px] grid grid-cols-1 c-lg:grid-cols-3 gap-[20px]'>
            {TESTIMONIALS.map((t, i) => (
              <figure key={i} className='rounded-[20px] bg-white border border-[#e8e6e1] px-[28px] py-[30px] flex flex-col'>
                <blockquote className='text-16 text-[#2a2a2a] leading-[1.6]'>&ldquo;{t.quote}&rdquo;</blockquote>
                <figcaption className='mt-[18px] flex items-center gap-[12px]'>
                  {t.avatar && <img src={t.avatar} alt='' className='w-[44px] h-[44px] rounded-full object-cover' loading='lazy' />}
                  <span className='flex flex-col leading-tight'>
                    <span className='text-15 font-semibold text-[#1a1a1a]'>{t.name}</span>
                    <span className='text-13 text-[#7a7a7a]'>{t.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
